const TRANSIENT = new Set([408, 502, 503, 504, 520, 521, 522, 523, 524]);

export async function requestJson(url, options = {}, {timeoutMs = 12000, retries = 0, label = 'Request'} = {}) {
  const readOnly = !options.method || options.method.toUpperCase() === 'GET';
  for (let attempt = 0; ; attempt += 1) {
    const controller = new AbortController();
    const cancel = () => controller.abort();
    const abortError = () => Object.assign(new Error('Request cancelled'), {name: 'AbortError'});
    if (options.signal?.aborted) throw abortError();
    options.signal?.addEventListener('abort', cancel, {once: true});
    let timer;
    try {
      const operation = (async () => {
        const response = await fetch(url, {...options, credentials: 'include', signal: controller.signal});
        const body = await response.json().catch(() => null);
        if (!response.ok) {
          const detail = body?.detail;
          const description = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(e => e?.msg || '').join('; ') : null;
          throw Object.assign(new Error(TRANSIENT.has(response.status)
            ? `${label} service is temporarily unavailable (HTTP ${response.status}). Please retry.`
            : description || `${label} failed (HTTP ${response.status}). Please retry.`), {status: response.status});
        }
        if (!body || typeof body !== 'object') throw new Error(`${label} returned an invalid response. Please retry.`);
        return body;
      })();
      const deadline = new Promise((_, reject) => {
        timer = setTimeout(() => {
          reject(Object.assign(new Error(`${label} is taking too long. Please retry.`), {timedOut: true}));
          controller.abort();
        }, timeoutMs);
      });
      return await Promise.race([operation, deadline]);
    } catch (error) {
      if (options.signal?.aborted) throw abortError();
      const retryable = error.timedOut || error instanceof TypeError || TRANSIENT.has(error.status);
      // Never replay sign-in or mutation requests after an ambiguous response.
      if (!(readOnly && retryable && attempt < retries)) {
        if (error instanceof TypeError) throw new Error(`${label} could not connect. Check your connection and retry.`);
        throw error;
      }
    } finally {
      clearTimeout(timer);
      options.signal?.removeEventListener('abort', cancel);
    }
    await new Promise(resolve => setTimeout(resolve, 400));
  }
}